import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Button,
  Divider,
  Empty,
    Popover,
    Menu
} from 'antd';
import {
  TagsOutlined,
  FilterOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import {SlidersHorizontal, Trash2, ListChecks, Copy} from 'lucide-react';
import useClipboard from "../../../hooks/useClipboard";
import { SPECIAL_TEXT_GRADIENT, SPECIAL_TEXT_ALT } from "../../../utils/styleUtils";

const { Title, Text } = Typography;
const { SubMenu } = Menu;

export const AttributeFilters = ({
  availableTagStats,
  activeFilters,
  onFilterChange,
  onClearFilters,
}) => {
  const filterCategories = [
    { key: 'asset_class', label: 'Asset Class' },
    { key: 'size', label: 'Size' },
    { key: 'style', label: 'Style' },
    { key: 'category', label: 'Category' },
  ];

  return (
    <Card bordered={false} className="gapless" title={(
        <Space>
          <SlidersHorizontal strokeWidth={1} />
          <span className={'text-xl'}>Available Portfolio Filters</span>
          {Object.keys(activeFilters).length > 0 && (
            <Button
              type="text"
              onClick={onClearFilters}
              size="small"
              className="text-red-400"
            >
              Clear All
            </Button>
          )}
      </Space>
    )}>
      <Menu mode="horizontal" className={'px-6'} selectable={false}>
        {filterCategories.map(({ key, label }) => {
          const categoryTags = availableTagStats[key] || {};
          if (!Object.keys(categoryTags).length) return null;

          return (
            <SubMenu key={key}
                     title={<span className={`${activeFilters?.[key]?.length??0>0 ? 'font-bold bg-gradient-to-r from-blue-500 to-cyan-900 bg-clip-text text-transparent':'text-black'}`}>
                       {label} ({activeFilters?.[key]?.length??0})
                     </span>}

            >
              {Object.entries(categoryTags).map(([tag, count]) => {
                const isActive = activeFilters[key]?.includes(tag);
                return (
                  <Menu.Item key={`${key}-${tag}`}>
                    <Popover content={`${tag.toUpperCase()} (${count})`}>
                      <Button
                        type="text"
                        onClick={() => {
                          const currentFilters = activeFilters[key] || [];
                          const newFilters = currentFilters.includes(tag)
                            ? currentFilters.filter(t => t !== tag)
                            : [...currentFilters, tag];
                          onFilterChange(key, newFilters);
                        }}
                        className={
                          isActive
                            ? 'bg-blue-100 text-blue-800 rounded-full'
                            : 'bg-transparent text-gray-500'
                        }
                      >
                        {tag.toUpperCase()} ({count})
                      </Button>
                    </Popover>
                  </Menu.Item>
                );
              })}
            </SubMenu>
          );
        })}
      </Menu>
    </Card>
  );
};
const SelectedPortfolios = ({ portfolios, selectedIds, onPortfolioDeselect }) => {
    const copyToClipboard = useClipboard();

  if (selectedIds.length === 0) return (
      <div className={'py-6'}>
        <Empty description={''} image={Empty.PRESENTED_IMAGE_SIMPLE}>
          No Portfolios Added
        </Empty>
      </div>
  );

  const popoverContent = (portfolio) => (
      <Space direction={'vertical'}>
        {Object.entries(portfolio.tags || {}).map(([category, value]) => (
            value && (
                <Tag key={`${portfolio.id}-${category}`} size="small" bordered={false} className={'bg-transparent'}>
                {value?.toUpperCase?.()}
              </Tag>
            )
          ))}
      </Space>
  )

  return (
    <Card bordered={false} title={(
        <Space>
          <ListChecks strokeWidth={1} />
          <span className={'text-xl'}>Selected Portfolios ({selectedIds.length})</span>
          <div className={'px-6'}>
            <Button variant={'text'} onClick={()=>copyToClipboard(JSON.stringify(selectedIds))}>
              <Copy strokeWidth={1} size={20} />
            </Button>
          </div>
      </Space>)}
    >

      <Space direction="vertical" className="w-full max-h-[80vh] pt-0 overflow-scroll hide-scrollbar">
        {portfolios.map(portfolio => (
            <Popover content={()=>popoverContent(portfolio)}>
              <div
                  key={portfolio.id}
                  className="flex justify-between items-center p-3 hover:bg-gray-50 rounded  border-b border-gray-200"
              >
                <Space direction="vertical" size="small">
                  <Row justify={'start'} align={'middle'}>
                    <Button
                        type="text"
                        icon={<Trash2 strokeWidth={1} color={'#f86363'} size={20}/>}
                        onClick={() => onPortfolioDeselect(portfolio.id, true)}
                    />
                    <Text strong>{portfolio.name}</Text>
                  </Row>
                </Space>

              </div>
            </Popover>
        ))}
      </Space>
    </Card>
  );
};

const PortfolioGrid = ({portfolios, onPortfolioSelect}) => {

  const getCategoryLabel = (label) => {
    if (label === 'style_auto_pct') return <span className={SPECIAL_TEXT_ALT}>Style Lean &trade;</span>;
    if (label === 'style_auto') return <span className={SPECIAL_TEXT_GRADIENT}>FINIAT Style&trade;</span>;
    return label.replace('_', ' ');
  }
  const getCategoryValue = (category, value) => {
    if (category === 'style_auto_pct') return value.toFixed(2);
    return value
  }
  return (
      <Row gutter={[16, 16]}>
        {portfolios.length === 0 ? (
            <Col span={24}>
          <Empty description="No portfolios match your filters" />
        </Col>
      ) : (
        portfolios.map(portfolio => (
          <Col key={portfolio.id} xs={24} sm={12} lg={8} xl={6}>
            <Card bordered={false}
              hoverable
              className="portfolio-card"
              onClick={() => onPortfolioSelect(portfolio.id)}
            >
              <Space direction="vertical" className="w-full">
                <Text className="mb-0 text-lg font-medium">
                  {portfolio.name}
                </Text>
                <div className="portfolio-tags">
                  {Object.entries(portfolio.tags || {}).map(([category, value]) => (
                    value && (
                        <Row align={'middle'} justify={'space-between'}>
                            <Col>
                                <Text className={'capitalize text-xs text-gray-800'}>
                                  {getCategoryLabel(category)}
                                </Text>
                            </Col>
                            <Col>
                                <Text style={{maxWidth:100}} ellipsis={{rows: 2}} className={'capitalize text-xs font-medium text-gray-800'}>
                                  {getCategoryValue(category, value)}
                                </Text>
                            </Col>
                        </Row>
                    )
                  ))}
                </div>
              </Space>
            </Card>
          </Col>
        ))
      )}
    </Row>
  );
};

const PathPortfolioManager = ({ selectedIds, onSubmit }) => {
  const allAvailablePortfolios = useSelector(state => state.portfolios.items);
  const [activeFilters, setActiveFilters] = React.useState({});

  // Get available portfolios (excluding selected ones)
  const availablePortfolios = useMemo(() => {
    return allAvailablePortfolios.filter(p => !selectedIds.includes(p.id));
  }, [allAvailablePortfolios, selectedIds]);

  // Get selected portfolios
  const selectedPortfolios = useMemo(() => {
    return allAvailablePortfolios.filter(p => selectedIds.includes(p.id));
  }, [allAvailablePortfolios, selectedIds]);

  // Calculate tag stats for available portfolios
  const availableTagStats = useMemo(() => {
    const stats = {};
    availablePortfolios.forEach(portfolio => {
      if (portfolio?.tags) {
        Object.entries(portfolio.tags).forEach(([category, value]) => {
          if (!value) return;
          if (!stats.hasOwnProperty(category)) {
            stats[category] = {};
          }
          stats[category][value] = (stats[category][value] || 0) + 1;
        });
      }
    });
    return stats;
  }, [availablePortfolios]);

  // Filter available portfolios based on active filters
  const filteredPortfolios = useMemo(() => {
    return availablePortfolios.filter(portfolio => {
      return Object.entries(activeFilters).every(([category, selectedTags]) => {
        if (!selectedTags.length) return true;
        return selectedTags.includes(portfolio.tags?.[category]);
      });
    });
  }, [availablePortfolios, activeFilters]);

  const handleFilterChange = (category, selectedTags) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: selectedTags
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  return (
      <div className={'max-h-screen'}>
          <Row gutter={24} >
              <Col xs={24} lg={6}>
                <SelectedPortfolios
                  portfolios={selectedPortfolios}
                  selectedIds={selectedIds}
                  onPortfolioDeselect={onSubmit}
                />
              </Col>
              <Col xs={24} lg={18}>
                  <AttributeFilters
                    availableTagStats={availableTagStats}
                    activeFilters={activeFilters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                <Divider/>
                  <div className={'max-h-[70vh] overflow-auto'}>
                      <PortfolioGrid portfolios={filteredPortfolios} onPortfolioSelect={onSubmit}/>
                  </div>

              </Col>
            </Row>
      </div>
  );
};

export default PathPortfolioManager;